import { getValue } from "@utils/lodash";
import React from "react";
import Input from "../Forms/Input";
import {
  formHandleBooleanArrayChange,
  formHandleDateArrayChange,
  formHandleFileArrayChange,
  formHandleLookeupArrayChange,
  formHandleNumericArrayChange,
  formHandleTextAreaArrayChange,
  formHandleTextArrayChange,
  formHandleTimeArrayChange,
} from "@common/handleChange";
import DateMonthYearPicker from "../Forms/Date";
import TextArea from "../Forms/textarea";
import { capitalizeFirstLetter } from "@common/text-helpers";
import TimePicker from "../Forms/Time";
import "./form-builder.scss";
import Radio from "../Forms/Radio";
import Checkbox from "../Forms/Checkbox";
import { generateFieldOptions } from "./generate-options";
import SingleSelectSearchDropdown from "../CustomSearchableDropdown/SingleSelectSearchDropdown";
import { getLookUpIdByName } from "./request-helper";

function ReimburseFormBuilder(props: any) {
  const {
    setFields,
    fields,
    simpleValidator,
    formOptions,
    mainInfo,
    formType = "standard",
  } = props;
  
  const getMinDate = (name: string) => {
    let duration_from =
      getValue(fields, `length`, 0) > 0
        ? fields.filter(
            (item: object) =>
              getValue(item, `field_name`, "") === "duration_from"
          )
        : [];
    switch (name) {
      case "duration_to":
        return getValue(duration_from, `[${0}].field_value`, "")
          ? new Date(getValue(duration_from, `[${0}].field_value`, ""))
          : null;
      default:
        return null;
    }
  };

  const normalizeField = (item: any) => {
    // Convert legacy format to standard format if needed
    if (formType === "legacy") {
      return {
        field_name: getValue(item, "field_name", ""),
        data_type: mapLegacyFieldType(getValue(item, "field_type", "")),
        sub_data_type: mapLegacySubType(getValue(item, "field_type", "")),
        field_value: getValue(item, "field_value", ""),
        display_name: getValue(item, "field_description", ""),
        is_mandatory: getValue(item, "required", "No") === "Yes",
        form_group: getValue(item, "form_group", ""),
        note: getValue(item, "note", ""),
      };
    }
    return item;
  };

  const mapLegacyFieldType = (fieldType: string) => {
    switch (fieldType) {
      case "text":
        return "text";
      case "textarea":
        return "text";
      case "select":
        return "picklist";
      case "checkbox":
        return "checkbox";
      case "datepicker":
        return "date";
      case "file":
        return "file";
      default:
        return fieldType;
    }
  };

  const mapLegacySubType = (fieldType: string) => {
    switch (fieldType) {
      case "text":
        return "single_line_text";
      case "textarea":
        return "multi_line_text";
      case "select":
        return "single_select";
      case "checkbox":
        return "boolean";
      case "datepicker":
        return "date";
      case "file":
        return "file";
      default:
        return "";
    }
  };

  const getLegacyFieldOptions = (item: any) => {
    const options = getValue(item, "field_value", {});
    if (typeof options === "object" && options !== null) {
      return Object.keys(options).map((key) => ({
        id: key,
        value: key,
        label: options[key],
        text: options[key],
      }));
    }
    return [];
  };

  const renderForm = (originalItem: any, index: number, formOptions: any) => {
    const item = normalizeField(originalItem);
    const isReportName = getValue(item, `field_name`, "") === "report_name";

    const fieldTypeKey =
      formType === "legacy"
        ? `${getValue(item, `data_type`, "")}-${getValue(
            item,
            `sub_data_type`,
            ""
          )}`
        : `${getValue(item, `data_type`, "")}-${getValue(
            item,
            `sub_data_type`,
            ""
          )}`;

    switch (fieldTypeKey) {
      case "text-single_line_text":
        return (
          <div className="form_builder_field">
            <Input
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleTextArrayChange(index, e, setFields)
              }
              value={
                isReportName
                  ? "This field will be auto-generated"
                  : getValue(item, `field_value`, "")
              }
              type={"text"}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              label={`${capitalizeFirstLetter(
                getValue(item, `display_name`, "")
              )}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={
                isReportName ? false : getValue(item, `is_mandatory`, false)
              }
              disabled={isReportName}
            />
          </div>
        );
      case "radio-boolean":
        return (
          <div className="form_builder_field">
            <Radio
              {...item}
              onChange={(e: any) =>
                formHandleBooleanArrayChange(
                  index,
                  e,
                  setFields,
                  getValue(item, `field_value`, "") === true ? false : true
                )
              }
              value={getValue(item, `field_value`, "")}
              checked={getValue(item, `field_value`, "") ? true : false}
              type={getValue(item, `data_type`, "")}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              label={`${capitalizeFirstLetter(
                getValue(item, `display_name`, "")
              )}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "checkbox-boolean":
        const htmlDescription = getValue(item, `field_description`, "");
        
        return (
          <div className="form_builder_field">
            <div className="checkbox-container">
              <Checkbox
                {...item}
                onChange={(e: any) =>
                  formHandleBooleanArrayChange(
                    index,
                    e,
                    setFields,
                    getValue(item, `field_value`, "") === true ? false : true
                  )
                }
                value={getValue(item, `field_value`, "")}
                checked={getValue(item, `field_value`, "") ? true : false}
                type={getValue(item, `data_type`, "")}
                placeholder={`Enter ${getValue(item, `display_name`, "")}`}
                label={capitalizeFirstLetter(
                  getValue(item, `display_name`, "")
                )}
                validator={
                  getValue(item, `is_mandatory`, false) && simpleValidator
                }
                mandatory={getValue(item, `is_mandatory`, false)}
              />
            </div>
            
            {/* Try an alternative approach to render HTML */}
            {htmlDescription && (
              <div 
                className="checkbox-description" 
                dangerouslySetInnerHTML={{ 
                  __html: htmlDescription 
                }}
              />
            )}
            
            {/* Debug output to see what's happening */}
            <div style={{ display: 'none' }}>
              {JSON.stringify(htmlDescription)}
            </div>
          </div>
        );
      case "text-multi_line_text":
        return (
          <div className="form_builder_field">
            <TextArea
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleTextAreaArrayChange(index, e, setFields)
              }
              value={getValue(item, `field_value`, "")}
              type={"textarea"}
              label={`${getValue(item, `display_name`, "")}`}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              rows={5}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "numerical-single_line_text":
      case "numerical-percentage":
      case "numerical-decimal":
      case "numerical-number":
        return (
          <div className="form_builder_field">
            <Input
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleNumericArrayChange(index, e, setFields)
              }
              value={getValue(item, `field_value`, "")}
              type={"number"}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              label={`${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "email-email":
        return (
          <div className="form_builder_field">
            <Input
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleTextArrayChange(index, e, setFields)
              }
              value={getValue(item, `field_value`, "")}
              type={"email"}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              label={`${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              validatoryType={
                getValue(item, `required`, false) && "required|email"
              }
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "phone-phone":
        return (
          <div className="form_builder_field">
            <Input
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleTextArrayChange(index, e, setFields)
              }
              type={"text"}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              label={`${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              validatoryType={
                getValue(item, `required`, false) && "required|min:10|max:10"
              }
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "url-url":
        return (
          <div className="form_builder_field">
            <Input
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleTextArrayChange(index, e, setFields)
              }
              value={getValue(item, `field_value`, "")}
              type={"url"}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              label={`${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "date-date":
        return (
          <div className="form_builder_field">
            <DateMonthYearPicker
              {...item}
              onChange={(e: any) =>
                formHandleDateArrayChange(index, e, setFields)
              }
              value={
                getValue(item, `field_value`, "")
                  ? getValue(item, `field_value`, "")
                  : null
              }
              type={"text"}
              label={`${getValue(item, `display_name`, "")}`}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={getValue(item, `is_mandatory`, false)}
              maxDate={new Date()}
              minDate={getMinDate(getValue(item, `field_name`, ""))}
            />
          </div>
        );
      case "date-date_time":
        return (
          <div className="form_builder_field">
            <TimePicker
              {...item}
              onChange={(e: any) =>
                formHandleTimeArrayChange(index, e, setFields)
              }
              value={
                getValue(item, `field_value`, "")
                  ? getValue(item, `field_value`, "")
                  : null
              }
              type={"text"}
              label={`${getValue(item, `display_name`, "")}`}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              showTimeInput
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "time-time":
      case "date-time":
      case "text-time":
        return (
          <div className="form_builder_field">
            <TimePicker
              {...item}
              onChange={(e: any) =>
                formHandleTimeArrayChange(index, e, setFields)
              }
              value={
                getValue(item, `field_value`, "")
                  ? getValue(item, `field_value`, "")
                  : null
              }
              type={"text"}
              label={`${getValue(item, `display_name`, "")}`}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              showTimeSelectOnly
              mandatory={getValue(item, `is_mandatory`, false)}
            />
          </div>
        );
      case "picklist-single_select":
        return (
          <div className="form_builder_field">
            <SingleSelectSearchDropdown
              {...item}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleLookeupArrayChange(index, e, setFields)
              }
              label={`label`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              width={"100%"}
              data={
                formType === "legacy"
                  ? getLegacyFieldOptions(originalItem)
                  : getValue(item, `is_custom_field`, false)
                  ? findCustomFieldPicklist(getValue(item, `field_name`, ""))
                  : generateFieldOptions(
                      getValue(item, `field_name`, ""),
                      getValue(
                        formOptions,
                        `[${getValue(item, `field_name`, "")}]`,
                        []
                      ),
                      []
                    )
              }
              value={getValue(item, `field_value`, "")}
              selectKey={"id"}
              name={getValue(item, `display_name`, "")}
              mandatory={getValue(item, `is_mandatory`, false)}
              showLabel
            />
          </div>
        );
      case "lookup-single_select":
        return (
          <div className="form_builder_field">
            <SingleSelectSearchDropdown
              {...item}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleLookeupArrayChange(index, e, setFields)
              }
              lookup_id={getLookUpIdByName(item)}
              label={`label`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              width={"100%"}
              value={getValue(item, `field_value`, "")}
              name={getValue(item, `display_name`, "")}
              lookup_api={getValue(item, `field_name`, "")}
              mandatory={getValue(item, `is_mandatory`, false)}
              showLabel
            />
          </div>
        );
      case "lookup-currency":
        return (
          <div className="form_builder_field">
            <SingleSelectSearchDropdown
              {...item}
              placeholder={`Enter ${getValue(item, `display_name`, "")}`}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                formHandleLookeupArrayChange(index, e, setFields)
              }
              label={`label`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              width={"100%"}
              value={getValue(item, `field_value`, "")}
              name={getValue(item, `display_name`, "")}
              lookup_api={getValue(item, `field_name`, "")}
              mandatory={getValue(item, `is_mandatory`, false)}
              showLabel
            />
          </div>
        );
      case "file-file":
        return (
          <div className="form_builder_field">
            <input
              {...item}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                formHandleFileArrayChange(index, e, setFields);
              }}
              type={"file"}
              placeholder={`Upload ${getValue(item, `display_name`, "")}`}
              label={`${getValue(item, `display_name`, "")}`}
              validator={
                getValue(item, `is_mandatory`, false) && simpleValidator
              }
              mandatory={getValue(item, `is_mandatory`, false)}
              note={getValue(item, `note`, "")}
            />
            {Array.isArray(getValue(item, `note`, "")) && (
              <div className="field-notes">
                {getValue(item, `note`, []).map((note: string, i: number) => (
                  <div key={i} dangerouslySetInnerHTML={{ __html: note }} />
                ))}
              </div>
            )}
            {typeof getValue(item, `note`, "") === "string" &&
              getValue(item, `note`, "") && (
                <div
                  className="field-notes"
                  dangerouslySetInnerHTML={{
                    __html: getValue(item, `note`, ""),
                  }}
                />
              )}
          </div>
        );
      default:
        return null;
    }
  };

  const findCustomFieldPicklist = (name: string) => {
    const data = getValue(mainInfo, `custom_list`, {});
    // Find key that includes "::custom_field_2"
    const targetKey = Object.keys(data).find((key) => key.endsWith(name));
    if (targetKey) {
      // Access the array if key is found
      const customFieldArray = data[targetKey];
      let array =
        getValue(customFieldArray, `length`, 0) > 0
          ? customFieldArray.map((item: object) => ({
              ...item,
              value: getValue(item, `id`, ""),
              label: getValue(item, `text`, ""),
            }))
          : [];
      return array;
    }
    return [];
  };

  const renderGroupedFields = () => {
    if (formType === "legacy") {
      // Group fields by form_group
      const groupedFields = fields.reduce((groups: any, field: any) => {
        const group = getValue(field, "form_group", "general");
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(field);
        return groups;
      }, {});

      return Object.keys(groupedFields).map((groupName, groupIndex) => (
        <div 
          key={groupIndex} 
          className={`form-group-section  child${groupIndex}`}
          style={groupIndex === 0 ? { borderRight: '1px solid #e0e0e0', paddingRight: '10px' } : {}}
        >          
          {/* <h3 className="form-group-title">
            {capitalizeFirstLetter(groupName.replace(/_/g, " "))}
          </h3> */}
          <div className="form-group-fields">
            {groupedFields[groupName].map((item: any, key: number) => {
              const originalIndex = fields.findIndex(
                (f: any) => f.field_name === item.field_name
              );
              return (
                <div className={`form_wrapper-pipeline`} key={key}>
                  {renderForm(item, originalIndex, formOptions)}
                </div>
              );
            })}
          </div>
        </div>
      ));
    }

    // Standard rendering for non-legacy format with 2 fields per row
    const rows = [];
    for (let i = 0; i < fields.length; i += 2) {
      rows.push(
        <div className="form-row" key={`row-${i}`}>
          <div className="form-col">
            <div className={`form_wrapper-pipeline`}>
              {renderForm(fields[i], i, formOptions)}
            </div>
          </div>
          {i + 1 < fields.length && (
            <div className="form-col">
              <div className={`form_wrapper-pipeline`}>
                {renderForm(fields[i + 1], i + 1, formOptions)}
              </div>
            </div>
          )}
        </div>
      );
    }
    
    return rows;
  };
  console.log(fields)
  return <div className="rendered-fields">{renderGroupedFields()}</div>;
}

export default ReimburseFormBuilder;
